import React, { useState, useEffect } from "react";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { useSelector } from "react-redux";
import { getParamsSelector } from "../Slices/ParamSlice";
import { ListView } from "@progress/kendo-react-listview";
import { Button } from "@progress/kendo-react-buttons";
import { getSettingsSelector, updateSettings } from "../../Settings/Slice";
import { useAppDispatch } from "../../Core/store.hooks";


const DashboardListView = ({ type }) => {
    const dispatch = useAppDispatch();
    const settings = useSelector(getSettingsSelector);

    const [dashboards, setDashboards] = useState([]);

    const getStorageKey = () => {
        return type === "machine" ? "savedDashboardMachine" : "savedDashboardCustomer";
    };

    const getDefaultLayout = () => {
        return type === "machine" ? settings.machineLayout : settings.customerLayout;
    };

    const getSessionLayout = () => {
        return type === "machine"
            ? settings.machineLayoutSession
            : settings.customerLayoutSession;
    };

    useEffect(() => {
        loadDashboards();
    }, [type]);

    const loadDashboards = () => {
        const storedDashboards = JSON.parse(localStorage.getItem(getStorageKey()));
        let names = [];
        if (storedDashboards && Array.isArray(storedDashboards)) {
            names = storedDashboards.map((d) => d.dbName);
        }

        names.unshift("App Default Dashboard");
        names.unshift("Tab Strip");
        setDashboards(names);
    };

    const deleteDashboard = (dashboardName) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the dashboard '${dashboardName}'?`
        );
        if (!confirmed) {
            return;
        }

        const updatedDashboards = dashboards.filter((name) => name !== dashboardName);
        setDashboards(updatedDashboards);

        const storedDashboards = JSON.parse(localStorage.getItem(getStorageKey()));
        const newStoredDashboards =
            storedDashboards && Array.isArray(storedDashboards)
                ? storedDashboards.filter((d) => d.dbName !== dashboardName)
                : [];
        localStorage.setItem(getStorageKey(), JSON.stringify(newStoredDashboards));
        let resetDefaultDashboard = false;
        let resetSessionDashboard = false;
        if (getDefaultLayout() === dashboardName) {
            resetDefaultDashboard = true;
        }
        if (getSessionLayout() === dashboardName) {
            resetSessionDashboard = true;
        }
        if (resetDefaultDashboard || resetSessionDashboard)
            setDashboard("Tab Strip", resetDefaultDashboard ,resetSessionDashboard);
    };

   
    const setDashboard = (dashboardName:string, setDefault,setSession) => {
        const updatedSettings = { ...settings };
        if (type === "machine") {
            if (setDefault) {
                updatedSettings.machineLayout = dashboardName;
            }
            if (setSession) {
                updatedSettings.machineLayoutSession = dashboardName;
            }
        } else {
            if (setDefault) {
                updatedSettings.customerLayout = dashboardName;
            }
            if (setSession) {
                updatedSettings.customerLayoutSession = dashboardName;
            }
        }
        localStorage.setItem("settings_serviceview", JSON.stringify(updatedSettings));
        dispatch(updateSettings(updatedSettings));
    }

    const itemRender = (itemProps) => {
        const dashboardName = itemProps.dataItem;
        const isFixed =
            dashboardName === "Tab Strip" || dashboardName === "App Default Dashboard";
        const isDefault = getDefaultLayout() === dashboardName;

        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    borderBottom: "1px solid #ccc",
                }}
            >
                {/* Dashboard name column */}
                <div style={{ flex: 1 }}>{dashboardName}</div>

                {/* Delete button column */}
                <div style={{ width: "120px", textAlign: "center", marginRight: "10px" }}>
                    <Button
                        onClick={() => {
                            if (!isFixed) {
                                deleteDashboard(dashboardName);
                            }
                        }}
                        type="button"
                        disabled={isFixed}
                        style={isFixed ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    >
                        Delete
                    </Button>
                </div>

                {/* Default status column */}
                <div style={{ width: "120px", textAlign: "center" }}>
                    {isDefault ? (
                        <span style={{ color: "green", fontWeight: "bold" }}>Is Default</span>
                    ) : (
                        <Button onClick={() => setDashboard(dashboardName,true,true)} type="button">
                            Set as Default
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <ListView data={dashboards} item={itemRender} />
    );
};


const ManageDashboards = () => {
    const globalParams = useSelector(getParamsSelector);
    const [selectedTab, setSelectedTab] = useState(0);

    useEffect(() => {
        if (globalParams.mainMachineSerialNo) {
            setSelectedTab(1); 
        } else {
            setSelectedTab(0);
        }
    }, [globalParams]);

    const handleSelect = (e) => {
        setSelectedTab(e.selected);
    };

    return (
        <TabStrip selected={selectedTab} onSelect={handleSelect}>
            <TabStripTab title="Customer Dashboards">
                <DashboardListView type="customer" />
            </TabStripTab>
            <TabStripTab title="Machine Dashboards">
                <DashboardListView type="machine" />
            </TabStripTab>
        </TabStrip>
    );
};

export default ManageDashboards;
